import React, { useState } from 'react';
import {
  Box, Stepper, Step, StepLabel, StepButton, Typography, Button,
  TextField, MenuItem, Grid, Paper, Chip, IconButton, Divider,
  Alert, Stack, FormControlLabel, Switch, InputAdornment,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  PhoneForwarded, Security, Speed, RequestQuote, CheckCircle,
  Add, Delete, ArrowBack, ArrowForward, SkipNext, Done, Refresh,
  Router, Lock, AccountTree,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../../services/api';

const STEPS = [
  { label: 'Carrier Profile' },
  { label: 'Authentication' },
  { label: 'Capacity & Media' },
  { label: 'Buy Rates', optional: true },
  { label: 'Review & Confirm' },
];

const CODECS = ['PCMU', 'PCMA', 'G729', 'G722', 'OPUS'];

function codeFromName(name: string): string {
  return (name || '').toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '').substring(0, 50);
}

const defaultForm = {
  name: '', code: '', host: '', port: 5060, protocol: 'UDP',
  auth_type: 'ip', username: '', password: '', from_user: '', from_domain: '',
  max_channels: 100, max_cps: 10, priority: 100, weight: 100,
  codec_list: ['PCMU', 'PCMA'] as string[],
  srtp: 'where_supported',
  currency: 'USD',
};

export function CarrierOnboardingPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [step, setStep] = useState(0);
  const [completed, setCompleted] = useState<Set<number>>(new Set());
  const [form, setForm] = useState<any>({ ...defaultForm });
  const [errors, setErrors] = useState<any>({});
  const [aclRows, setAclRows] = useState<any[]>([]);
  const [rateRows, setRateRows] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<any>(null);

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const codeAuto = form.code || codeFromName(form.name);

  const validateStep = (s: number): boolean => {
    const e: any = {};
    if (s === 0) {
      if (!form.name.trim()) e.name = 'Required';
      if (!form.host.trim()) e.host = 'Required (IP or FQDN)';
    }
    if (s === 1 && form.auth_type === 'digest') {
      if (!form.username.trim()) e.username = 'Required for digest auth';
      if (!form.password.trim()) e.password = 'Required for digest auth';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep(step)) return;
    setCompleted(prev => new Set([...prev, step]));
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep(s => Math.max(s - 1, 0));
  const skip = () => { setCompleted(prev => new Set([...prev, step])); setStep(s => s + 1); };

  const toggleCodec = (c: string) =>
    set('codec_list', form.codec_list.includes(c)
      ? form.codec_list.filter((x: string) => x !== c)
      : [...form.codec_list, c]);

  const addAcl = () => setAclRows(prev => [...prev, { ip_addr: '', mask: 32, port: 0, description: '', _id: Date.now() }]);
  const removeAcl = (id: number) => setAclRows(prev => prev.filter(a => a._id !== id));
  const updateAcl = (id: number, k: string, v: any) => setAclRows(prev => prev.map(a => a._id === id ? { ...a, [k]: v } : a));

  const addRate = () => setRateRows(prev => [...prev, { destination: '', destination_name: '', buy_rate: '', _id: Date.now() }]);
  const removeRate = (id: number) => setRateRows(prev => prev.filter(r => r._id !== id));
  const updateRate = (id: number, k: string, v: any) => setRateRows(prev => prev.map(r => r._id === id ? { ...r, [k]: v } : r));

  const submit = async () => {
    if (!validateStep(0)) { setStep(0); return; }
    setSubmitting(true);
    try {
      const payload: any = {
        name: form.name, code: codeAuto, host: form.host.trim(),
        port: Number(form.port) || 5060, protocol: form.protocol,
        auth_type: form.auth_type,
        username: form.auth_type === 'digest' ? form.username : undefined,
        password: form.auth_type === 'digest' ? form.password : undefined,
        from_user: form.from_user || undefined,
        from_domain: form.from_domain || undefined,
        max_channels: Number(form.max_channels) || 100,
        max_cps: Number(form.max_cps) || 10,
        priority: Number(form.priority) || 100,
        weight: Number(form.weight) || 100,
        codec_list: form.codec_list,
        srtp: form.srtp,
        currency: form.currency,
        acl_entries: aclRows.filter(a => a.ip_addr).map(a => ({
          ip_addr: a.ip_addr, mask: Number(a.mask) || 32,
          port: Number(a.port) || 0, description: a.description || undefined,
        })),
        rates: rateRows.filter(r => r.destination && r.buy_rate !== '').map(r => ({
          destination: r.destination, destination_name: r.destination_name || undefined,
          buy_rate: Number(r.buy_rate) || 0,
        })),
      };
      const { data } = await api.post('/carriers/onboard', payload);
      setSuccess(data);
      enqueueSnackbar('Carrier onboarded — Kamailio routing synced', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to onboard carrier', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step renderers ────────────────────────────────────────────────────────

  const renderStep0 = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Alert severity="info" icon={<Router />}>
          A carrier is an upstream <strong>termination</strong> trunk (e.g. BT, NGN, QXTEL). LCR picks the
          cheapest available carrier per destination, with automatic failover.
        </Alert>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Carrier Name *" value={form.name}
          onChange={e => set('name', e.target.value)}
          error={!!errors.name} helperText={errors.name} placeholder="BT Wholesale" />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Code" value={codeAuto}
          onChange={e => set('code', e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
          helperText="Auto-derived from name — used as the trunk identifier"
          InputProps={{ endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Reset from name">
                <IconButton size="small" onClick={() => set('code', '')}><Refresh fontSize="small" /></IconButton>
              </Tooltip>
            </InputAdornment>
          )}} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Signaling Host (IP or FQDN) *" value={form.host}
          onChange={e => set('host', e.target.value)}
          error={!!errors.host} helperText={errors.host || 'Where we send INVITEs (carrier SBC address)'} />
      </Grid>
      <Grid item xs={6} md={3}>
        <TextField fullWidth label="Port" type="number" value={form.port}
          onChange={e => set('port', e.target.value)} />
      </Grid>
      <Grid item xs={6} md={3}>
        <TextField fullWidth select label="Transport" value={form.protocol}
          onChange={e => set('protocol', e.target.value)}>
          {['UDP', 'TCP', 'TLS'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
      </Grid>
    </Grid>
  );

  const renderStep1 = () => (
    <Box>
      <Typography variant="subtitle2" gutterBottom>How does this carrier authenticate us?</Typography>
      <ToggleButtonGroup exclusive value={form.auth_type} sx={{ mb: 2 }}
        onChange={(_, v) => v && set('auth_type', v)}>
        <ToggleButton value="ip"><Security sx={{ mr: 1 }} fontSize="small" /> IP Authentication</ToggleButton>
        <ToggleButton value="digest"><Lock sx={{ mr: 1 }} fontSize="small" /> Digest / Registration</ToggleButton>
      </ToggleButtonGroup>

      {form.auth_type === 'ip' ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          The carrier trusts our source IP <strong>46.225.128.180</strong>. Their signaling host
          (<strong>{form.host || 'set in step 1'}</strong>) is auto-added to the Kamailio inbound ACL.
        </Alert>
      ) : (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Username *" value={form.username}
              onChange={e => set('username', e.target.value)}
              error={!!errors.username} helperText={errors.username} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Password *" value={form.password}
              onChange={e => set('password', e.target.value)}
              error={!!errors.password} helperText={errors.password} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="From User (optional)" value={form.from_user}
              onChange={e => set('from_user', e.target.value)}
              helperText="Override the From-user sent to this carrier" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="From Domain (optional)" value={form.from_domain}
              onChange={e => set('from_domain', e.target.value)} />
          </Grid>
        </Grid>
      )}

      <Divider sx={{ my: 2 }}><Chip label="Additional Signaling Source IPs (ACL)" size="small" /></Divider>
      <Alert severity="info" sx={{ mb: 2 }}>
        Add extra IPs the carrier may <em>send</em> from (if different from the host above). All are trusted
        in the Kamailio inbound ACL and reloaded on save.
      </Alert>
      <Button startIcon={<Add />} variant="outlined" onClick={addAcl} sx={{ mb: 2 }}>Add Source IP</Button>
      {aclRows.length > 0 && (
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>IP Address</TableCell><TableCell>Mask</TableCell><TableCell>Port</TableCell>
            <TableCell>Description</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {aclRows.map(a => (
              <TableRow key={a._id}>
                <TableCell><TextField size="small" value={a.ip_addr} placeholder="185.107.57.25"
                  onChange={e => updateAcl(a._id, 'ip_addr', e.target.value)} /></TableCell>
                <TableCell><TextField size="small" type="number" value={a.mask} sx={{ width: 72 }}
                  onChange={e => updateAcl(a._id, 'mask', e.target.value)} inputProps={{ min: 1, max: 32 }} /></TableCell>
                <TableCell><TextField size="small" type="number" value={a.port} sx={{ width: 90 }}
                  onChange={e => updateAcl(a._id, 'port', e.target.value)} helperText="0=any" /></TableCell>
                <TableCell><TextField size="small" value={a.description} placeholder="e.g. media IP"
                  onChange={e => updateAcl(a._id, 'description', e.target.value)} /></TableCell>
                <TableCell><IconButton size="small" color="error" onClick={() => removeAcl(a._id)}>
                  <Delete fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );

  const renderStep2 = () => (
    <Grid container spacing={2}>
      <Grid item xs={6} md={3}>
        <TextField fullWidth label="Max Channels" type="number" value={form.max_channels}
          onChange={e => set('max_channels', e.target.value)} helperText="Concurrent call cap" />
      </Grid>
      <Grid item xs={6} md={3}>
        <TextField fullWidth label="Max CPS" type="number" value={form.max_cps}
          onChange={e => set('max_cps', e.target.value)} helperText="Calls per second" />
      </Grid>
      <Grid item xs={6} md={3}>
        <TextField fullWidth label="Priority" type="number" value={form.priority}
          onChange={e => set('priority', e.target.value)} helperText="Lower = preferred" />
      </Grid>
      <Grid item xs={6} md={3}>
        <TextField fullWidth label="Weight" type="number" value={form.weight}
          onChange={e => set('weight', e.target.value)} helperText="Load-balance within priority" />
      </Grid>
      <Grid item xs={12}><Divider><Chip label="Codecs" size="small" /></Divider></Grid>
      <Grid item xs={12}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {CODECS.map(c => (
            <Chip key={c} label={c} clickable
              color={form.codec_list.includes(c) ? 'primary' : 'default'}
              variant={form.codec_list.includes(c) ? 'filled' : 'outlined'}
              onClick={() => toggleCodec(c)} />
          ))}
        </Stack>
      </Grid>
      <Grid item xs={12}><Divider><Chip label="Media Security" size="small" /></Divider></Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth select label="SRTP Policy" value={form.srtp}
          onChange={e => set('srtp', e.target.value)}>
          <MenuItem value="where_supported">SRTP where supported (RTP fallback)</MenuItem>
          <MenuItem value="mandatory">Mandatory SRTP (strict)</MenuItem>
          <MenuItem value="none">Plain RTP</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <Alert severity="info" sx={{ height: '100%' }}>
          Platform default is <strong>SRTP where supported</strong>, applied at the media layer (RTPEngine).
        </Alert>
      </Grid>
    </Grid>
  );

  const renderStep3 = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Optionally add a few <strong>buy rates</strong> (carrier cost per destination prefix). These drive
        LCR. For a full rate deck, skip here and use <strong>Rate Card / Billing Import</strong> after onboarding.
      </Alert>
      <Button startIcon={<Add />} variant="outlined" onClick={addRate} sx={{ mb: 2 }}>Add Buy Rate</Button>
      {rateRows.length === 0 ? (
        <Typography color="text.secondary">No buy rates — skip and import the full deck later.</Typography>
      ) : (
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Destination Prefix</TableCell><TableCell>Name</TableCell>
            <TableCell>Buy Rate ({form.currency}/min)</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {rateRows.map(r => (
              <TableRow key={r._id}>
                <TableCell><TextField size="small" value={r.destination} placeholder="44 or 4420"
                  onChange={e => updateRate(r._id, 'destination', e.target.value)} /></TableCell>
                <TableCell><TextField size="small" value={r.destination_name} placeholder="UK / UK London"
                  onChange={e => updateRate(r._id, 'destination_name', e.target.value)} /></TableCell>
                <TableCell><TextField size="small" type="number" value={r.buy_rate} placeholder="0.0045"
                  onChange={e => updateRate(r._id, 'buy_rate', e.target.value)}
                  inputProps={{ step: '0.0001' }} sx={{ width: 130 }} /></TableCell>
                <TableCell><IconButton size="small" color="error" onClick={() => removeRate(r._id)}>
                  <Delete fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );

  const renderReview = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            <PhoneForwarded fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />Carrier
          </Typography>
          <Stack spacing={0.5}>
            <Row label="Name" value={form.name} />
            <Row label="Code" value={codeAuto} />
            <Row label="Host" value={`${form.host}:${form.port} (${form.protocol})`} />
            <Row label="Auth" value={form.auth_type === 'ip' ? 'IP-based' : `Digest (${form.username})`} />
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            <Speed fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />Capacity & Media
          </Typography>
          <Stack spacing={0.5}>
            <Row label="Max Channels" value={form.max_channels} />
            <Row label="Max CPS" value={form.max_cps} />
            <Row label="Priority / Weight" value={`${form.priority} / ${form.weight}`} />
            <Row label="Codecs" value={form.codec_list.join(', ')} />
            <Row label="SRTP" value={form.srtp.replace(/_/g, ' ')} />
          </Stack>
        </Paper>
      </Grid>
      {aclRows.filter(a => a.ip_addr).length > 0 && (
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              <Security fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />Extra ACL IPs
            </Typography>
            {aclRows.filter(a => a.ip_addr).map((a, i) => (
              <Typography key={i} variant="body2">{a.ip_addr}/{a.mask}{a.description ? ` — ${a.description}` : ''}</Typography>
            ))}
          </Paper>
        </Grid>
      )}
      {rateRows.filter(r => r.destination && r.buy_rate !== '').length > 0 && (
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              <RequestQuote fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />Buy Rates
            </Typography>
            {rateRows.filter(r => r.destination && r.buy_rate !== '').map((r, i) => (
              <Typography key={i} variant="body2">{r.destination} — {form.currency} {r.buy_rate}/min{r.destination_name ? ` (${r.destination_name})` : ''}</Typography>
            ))}
          </Paper>
        </Grid>
      )}
      <Grid item xs={12}>
        <Alert severity="success" icon={<AccountTree />}>
          On confirm: carrier is created, added to the LCR gateway list, ACL updated, and
          <strong> Kamailio routing hot-reloaded</strong> — no restart needed.
        </Alert>
      </Grid>
    </Grid>
  );

  const stepContent = [renderStep0, renderStep1, renderStep2, renderStep3, renderReview];

  if (success) {
    return (
      <Box sx={{ p: 3, maxWidth: 700, mx: 'auto', mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle color="success" sx={{ fontSize: 72, mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>Carrier Onboarded!</Typography>
          <Typography color="text.secondary" gutterBottom>{success.name} is now a routing gateway.</Typography>
          <Stack spacing={1} sx={{ mt: 2 }} alignItems="center">
            <Chip label={`Gateway #${success.gw_id}`} color="primary" />
            {success.rates_created > 0 && <Chip label={`${success.rates_created} buy rate(s)`} color="success" />}
            {success.acl_entries_created > 0 && <Chip label={`${success.acl_entries_created} extra ACL IP(s)`} color="info" />}
            <Chip label={success.kamailio_reloaded ? 'Kamailio reloaded ✓' : 'Kamailio reload pending'}
              color={success.kamailio_reloaded ? 'success' : 'warning'} />
            {success.sync && <Typography variant="caption" color="text.secondary">{success.sync}</Typography>}
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
            <Button variant="contained" onClick={() => navigate('/carriers')}>View Carriers</Button>
            <Button variant="outlined" onClick={() => {
              setSuccess(null); setForm({ ...defaultForm }); setAclRows([]); setRateRows([]);
              setStep(0); setCompleted(new Set());
            }}>Onboard Another</Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <PhoneForwarded color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Carrier Onboarding</Typography>
          <Typography variant="body2" color="text.secondary">
            Add an upstream termination carrier to the LCR routing engine
          </Typography>
        </Box>
      </Stack>

      <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
        {STEPS.map((s, i) => (
          <Step key={i} completed={completed.has(i)}>
            <StepButton onClick={() => { if (completed.has(i) || i < step) setStep(i); }}>
              <StepLabel optional={s.optional ? <Typography variant="caption">Optional</Typography> : undefined}>
                {s.label}
              </StepLabel>
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <Paper variant="outlined" sx={{ p: 3, minHeight: 300, mb: 3 }}>
        {stepContent[step]()}
      </Paper>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button startIcon={<ArrowBack />} onClick={back} disabled={step === 0}>Back</Button>
        <Stack direction="row" spacing={2}>
          {STEPS[step]?.optional && step < STEPS.length - 1 && (
            <Button startIcon={<SkipNext />} variant="outlined" onClick={skip}>Skip</Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button endIcon={<ArrowForward />} variant="contained" onClick={next}>Next</Button>
          ) : (
            <Button startIcon={<Done />} variant="contained" color="success" onClick={submit}
              disabled={submitting} sx={{ px: 4 }}>
              {submitting ? 'Onboarding…' : 'Onboard Carrier'}
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

function Row({ label, value }: { label: string; value: any }) {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>{label}:</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
